<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted } from 'vue'

import { SliderType } from '@xcel-next/rendering-tools'

import { useConfigStore } from '../../stores/config'
import { usePageStore } from '../../stores/page'
import { useFlickitySlider, flickitySliderProps } from '../../composables/use-flickity-slider'
import { useAdaMoltenBundle } from '../../composables/use-ada-molten-bundle'
import { useDeviceDetection } from '../../composables/use-device-detection'
import { useAdaDynamicSlotMarker } from '../../composables/use-ada-dynamic-slot-marker'
import { shouldPrioritizeTeaserImage } from '../../utils/image-priority'
import { shouldUseRecipeTeaserStyles } from '../../utils/teaser-group'
import Teaser from './Teaser.vue'
import EventBus from '../../event-bus/EventBus'

import type { TeaserObject } from '../../composables/use-teaser'
import type { Brand } from '@xcel-next/core/enums'

// useAdaDynamicSlotMarker matches components by getCurrentInstance().type.name === 'TeaserGroup'
defineOptions({ name: 'TeaserGroup' })

const props = withDefaults(defineProps<{
  sliderNo?: number,
  initialIndex?: number,
  teaserGroupType?: string,
  teasers?: TeaserObject[],
  headline?: string,
  headlineType?: string,
  introductionText?: string,
  highlighted?: boolean,
  highlightColor?: string,
  brand?: Brand,
  includeDepth?: number,
  listOrderPosition?: number,
  dataIsSliderChild?: boolean,
  isSliderOverview?: boolean,
  dataSliderInitialSlide?: boolean,
  isPreview?: boolean,
  prioritizeFirstImage?: boolean
}>(), {
  sliderNo: flickitySliderProps.sliderNo.default,
  initialIndex: flickitySliderProps.initialIndex.default,
  teaserGroupType: '',
  headline: '',
  headlineType: '',
  introductionText: '',
  highlighted: false,
  highlightColor: '',
  includeDepth: 0,
  listOrderPosition: 0,
  dataIsSliderChild: false,
  isSliderOverview: false,
  dataSliderInitialSlide: false,
  isPreview: false,
  prioritizeFirstImage: false
})

const flickityDiv = ref<HTMLElement | null>(null)
const { deviceType } = useDeviceDetection()
const registerSlots = ref<string[]>([])
const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })
const { sliderSettings, sliderControlDescription, initFlickitySlider, checkButtonPosition, keepTabbingFocusInSlider } = useFlickitySlider(flickityDiv)
useAdaDynamicSlotMarker(props.teaserGroupType)

// set once from the initial prop, matching the previous options-API `data()` behaviour
const isTeaserSlider = props.teaserGroupType.includes('slider')

// Some of these store refs are also or only used by mixins
const { rsConfig } = storeToRefs(useConfigStore())
const { isArticleOrArticleVersion } = storeToRefs(usePageStore())

const flickitySliderId = computed(() => (isTeaserSlider ? `paragraph-type-teaser-group-flickity-${props.sliderNo}` : undefined)) // in vue3 the id field will only be removed if value is null/undefined
const specialBrandTeaserEnabled = computed(() => rsConfig.value.dynamicComponents?.teaser)
const headlineTagType = computed(() => (isArticleOrArticleVersion.value ? 'span' : 'h2'))
const teaserGroupSliderConfig = computed(() =>
  (props.teaserGroupType.includes('slider') ? sliderSettings.value?.teaserGroupSlider?.[props.teaserGroupType as SliderType] : undefined) ?? { teaserGroupType: '', customOptions: {} }
)
const useRecipeTeaserStyles = computed(() => shouldUseRecipeTeaserStyles(props.teaserGroupType, props.teasers ?? []))
const setStyleClasses = computed(() => ({
  'bx-teaser-container': true,
  [teaserGroupSliderConfig.value.teaserGroupType]: teaserGroupSliderConfig.value.teaserGroupType && isTeaserSlider && !useRecipeTeaserStyles.value,
  [props.teaserGroupType]: true,
  'bx-teaser-container--recipe': useRecipeTeaserStyles.value,
  'bx-teaser-container--highlight': props.highlighted,
  [`bx-teaser-container--${props.highlightColor}`]: !!props.highlightColor,
  'bx-teaser-container--special-brand': specialBrandTeaserEnabled.value,
  'bx-teaser-container--slider-overview': props.isSliderOverview
}))

const prioritizeTeaserImage = (teaserOrderPosition: number) => shouldPrioritizeTeaserImage(
  isArticleOrArticleVersion.value,
  isTeaserSlider,
  props.includeDepth,
  props.listOrderPosition,
  teaserOrderPosition,
  props.prioritizeFirstImage
)

const triggerGAForTeaser = (index: number) => {
  const dataLayerEvent: Record<string, unknown> = {
    event: 'custom_gallery_slide_pageview',
    slide_number: (index + 1), // index starts at 0 - actual counting starts from 1
    slide_type: 'teaser'
  }
  dataLayerEvent.slide_teaser_url = props.teasers?.[index]?.url
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(dataLayerEvent)
}

onMounted(async () => {
  if (!isTeaserSlider) return

  // while we want to track page impressions and ad reloads for teaser sliders, we need to extend the flickity config
  const extendedSingleTeaserGroupSliderConfig = {
    freeScroll: false,
    on: {
      ready: () => {
        checkButtonPosition()
      },
      change: (index: number) => {
        // A single requestAnimationFrame still fires BEFORE the browser paints, so anything
        // here was still blocking the slide-change paint (bad for INP). Nesting a 2nd rAF
        // guarantees the callback runs after the paint has happened.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!props.isPreview && index !== props.initialIndex && sliderSettings.value?.triggerGAOnSlide) {
              triggerGAForTeaser(index)
            }

            // HEAVY work: ad reload, pushed onto a macrotask so it can't extend the current frame
            const reloadAdsOnInteraction = rsConfig.value.adSlotUsage?.reloadAdsOnInteraction
            if (reloadAdsOnInteraction?.enabled) {
              const onAdReload = () => {
                if (sliderSettings.value?.triggerAGFOnAdReload) EventBus.$emit('triggerAGF')
              }
              setTimeout(() => {
                adaSlotReloadOnInteraction(reloadAdsOnInteraction.config, onAdReload)
              }, 0)
            }
          })
        })
      }
    }
  }
  // but only want to extend it for single teaser sliders
  const flickityConfig = props.teaserGroupType === 'bx-teaser-container--slider-single'
    ? { ...teaserGroupSliderConfig.value.customOptions, ...extendedSingleTeaserGroupSliderConfig }
    : teaserGroupSliderConfig.value.customOptions
  await initFlickitySlider(flickityConfig)
})
</script>

<template v-if="teasers && teasers.length > 0">
  <div :class="setStyleClasses">
    <div v-if="headline || introductionText"
         class="bx-teaser-container__header">
      <component :is="headlineTagType"
                 v-if="headline"
                 :class="[headlineType, 'bx-teaser-container__header-title', 'bx-typo--teaser-container-header-title']">
        <span class="bx-teaser-container__header-title-text">
          {{ headline }}
        </span>
      </component>
      <div v-if="introductionText"
           class="bx-teaser-container__header-kicker bx-typo--content-continuous-text bx-content__continuous-text bx-typo--teaser-container-header-kicker"
           v-html="introductionText" />
    </div>
    <div v-else
         class="bx-teaser-container__header bx-teaser-container__header--empty" />
    <!-- eslint-disable vue/no-unused-refs MIXIN -->
    <div :id="flickitySliderId"
         ref="flickityDiv"
         :class="{
           'bx-teaser-container__wrapper': !isTeaserSlider,
           'bx-slider': isTeaserSlider,
           'bx-slider--teaser-group': isTeaserSlider,
           'bx-js-hide-buttons': isTeaserSlider
         }"
         :aria-label="`Empfohlene Inhalte Karusell: ${teasers?.length} Inhalte. ${sliderControlDescription}`"
         @keydown.enter="keepTabbingFocusInSlider()">
      <template v-for="(teaser, index) in teasers">
        <BrandTeaser v-if="specialBrandTeaserEnabled"
                     :key="`${index}-special`"
                     :composed-teaser="teaser"
                     :include-depth="isTeaserSlider ? includeDepth + 1 : includeDepth"
                     :list-order-position="listOrderPosition ?? undefined"
                     :data-is-slider-child="dataIsSliderChild"
                     :data-slider-initial-slide="isTeaserSlider ? index === 0 : dataSliderInitialSlide"
                     :prioritize-image="prioritizeTeaserImage(index)"
                     :teaser-order-position="index"
                     :data-cell-index="index"
                     :meta-data="{brand: brand ?? undefined, teaserGroupType}"
                     :data-button-position-logic="isTeaserSlider ? 'image' : false"
                     :class="{'bx-slider__cell': isTeaserSlider}" />
        <Teaser v-else
                :key="index"
                :composed-teaser="teaser"
                :include-depth="isTeaserSlider ? includeDepth + 1 : includeDepth"
                :list-order-position="listOrderPosition ?? undefined"
                :data-cell-index="index"
                :data-is-slider-child="dataIsSliderChild"
                :data-slider-initial-slide="isTeaserSlider ? index === 0 : dataSliderInitialSlide"
                :prioritize-image="prioritizeTeaserImage(index)"
                :meta-data="{brand: brand ?? undefined, teaserGroupType}"
                :data-button-position-logic="isTeaserSlider ? 'image' : null"
                :class="{'bx-slider__cell': isTeaserSlider}" />
      </template>
    </div>
  </div>
</template>
